import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
    PieController,
    ArcElement,
    Filler,
    type ChartOptions,
} from 'chart.js';
import { apiFetch } from '../utils/apiFetch';
import { useParams, useNavigate } from 'react-router-dom';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
    PieController,
    ArcElement,
    Filler
);

interface Statistic {
    hour: number;
    total: number;
    accepted: number;
    not_accepted: number;
    invalid_code: number;
    CTSTREET_accepted: number;
    ONLINE_accepted: number;
    WORKER_accepted: number;
    FREE_accepted: number;
    CTSTREET_not_accepted: number;
    ONLINE_not_accepted: number;
    WORKER_not_accepted: number;
    FREE_not_accepted: number;
}

type AcceptedOrNot = 'accepted' | 'not_accepted';
type TypeKeys = 'CTSTREET' | 'ONLINE' | 'WORKER' | 'FREE';

const TYPE_CONFIG: Record<
    TypeKeys,
    {
        label: string;
        color: string;
        background: string;
    }
> = {
    CTSTREET: {
        label: 'CTSTREET',
        color: '#2563eb',
        background: '#dbeafe',
    },
    ONLINE: {
        label: 'ONLINE',
        color: '#7c3aed',
        background: '#ede9fe',
    },
    WORKER: {
        label: 'WORKER',
        color: '#d97706',
        background: '#fef3c7',
    },
    FREE: {
        label: 'FREE',
        color: '#059669',
        background: '#d1fae5',
    },
};

const COLORS = {
    accepted: '#10b981',
    acceptedLight: '#d1fae5',
    rejected: '#f43f5e',
    rejectedLight: '#ffe4e6',
    invalid: '#f59e0b',
    invalidLight: '#fef3c7',
    primary: '#111827',
    muted: '#64748b',
    grid: '#e2e8f0',
};

const formatNumber = (value: number) =>
    value.toLocaleString('es-ES');

const EstadisticasEvento: React.FC = () => {
    const { eventId, clientId } = useParams();
    const navigate = useNavigate();
    const { token } = useContext(AuthContext);

    const eventIdNum = Number(eventId);

    const [statistics, setStatistics] = useState<Statistic[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [title, setTitle] = useState<string>('');

    const fetchStatistics = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams({
                id_event: eventIdNum.toString(),
                id_client: clientId || '',
            });

            const response = await apiFetch(
                `web/event_statistics?${queryParams.toString()}`,
                {
                    method: 'GET',
                }
            );

            if (!response.ok) {
                navigate('/main/not-found');
                return;
            }

            const data = await response.json();

            setTitle(data.title || '');

            if (Array.isArray(data.statistics)) {
                console.log(data.statistics);
                setStatistics(data.statistics);
            } else {
                throw new Error('Datos inesperados recibidos.');
            }
        } catch (err) {
            const errorMsg =
                err instanceof Error
                    ? err.message
                    : 'Error desconocido';

            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [eventIdNum, clientId, navigate, token]);

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    const calculateTotal = useCallback(
        (dataKey: keyof Statistic) =>
            statistics.reduce(
                (sum, stat) => sum + Number(stat[dataKey] || 0),
                0
            ),
        [statistics]
    );

    const totals = useMemo(() => {
        const accepted = {
            CTSTREET: calculateTotal('CTSTREET_accepted'),
            ONLINE: calculateTotal('ONLINE_accepted'),
            WORKER: calculateTotal('WORKER_accepted'),
            FREE: calculateTotal('FREE_accepted'),
        };

        const rejected = {
            CTSTREET: calculateTotal('CTSTREET_not_accepted'),
            ONLINE: calculateTotal('ONLINE_not_accepted'),
            WORKER: calculateTotal('WORKER_not_accepted'),
            FREE: calculateTotal('FREE_not_accepted'),
        };

        const total = calculateTotal('total');
        const acceptedTotal = calculateTotal('accepted');
        const rejectedTotal = calculateTotal('not_accepted');
        const invalid = calculateTotal('invalid_code');

        const acceptanceRate =
            total > 0
                ? Math.round((acceptedTotal / total) * 100)
                : 0;

        return {
            accepted,
            rejected,
            acceptedTotal,
            rejectedTotal,
            invalid,
            total,
            acceptanceRate,
        };
    }, [calculateTotal]);

    const generateBarData = (
        dataKey: keyof Statistic,
        color: string
    ) => ({
        labels: statistics.map((stat) => `${stat.hour}`),
        datasets: [
            {
                label: 'Escaneos',
                data: statistics.map((stat) =>
                    Number(stat[dataKey] || 0)
                ),
                backgroundColor: color,
                borderRadius: 6,
                borderSkipped: false,
                maxBarThickness: 34,
            },
        ],
    });

    const barOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 500,
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                displayColors: false,
                backgroundColor: '#111827',
                padding: 12,
                cornerRadius: 8,
                titleFont: {
                    size: 12,
                    weight: 600,
                },
                bodyFont: {
                    size: 13,
                },
                callbacks: {
                    title: (tooltipItems) => {
                        const hour = Number(tooltipItems[0]?.label ?? 0);
                        const nextHour = (hour + 1) % 24;

                        return `${hour}h - ${nextHour}h`;
                    },
                    label: (tooltipItem) => {
                        const value = Number(tooltipItem.raw || 0);

                        return value === 1
                            ? '1 escaneo'
                            : `${formatNumber(value)} escaneos`;
                    },
                },
            },
        },
        scales: {
            x: {
                border: {
                    display: false,
                },
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#94a3b8',
                    font: {
                        size: 11,
                    },
                    padding: 8,
                    callback: (value) => `${value}h`,
                },
            },
            y: {
                beginAtZero: true,
                border: {
                    display: false,
                },
                grid: {
                    color: COLORS.grid,
                },
                ticks: {
                    color: '#94a3b8',
                    font: {
                        size: 11,
                    },
                    precision: 0,
                    padding: 8,
                },
            },
        },
    };

    const generatePieData = (
        acceptedOrNot: AcceptedOrNot,
        includeInvalid = false
    ) => {
        const types: TypeKeys[] = [
            'CTSTREET',
            'ONLINE',
            'WORKER',
            'FREE',
        ];

        const values = types.map((type) =>
            calculateTotal(
                `${type}_${acceptedOrNot}` as keyof Statistic
            )
        );

        const labels = types.map(
            (type) => TYPE_CONFIG[type].label
        );

        const colors = types.map(
            (type) => TYPE_CONFIG[type].color
        );

        if (includeInvalid) {
            labels.push('Inválidos');
            values.push(totals.invalid);
            colors.push(COLORS.invalid);
        }

        return {
            labels,
            datasets: [
                {
                    data: values,
                    backgroundColor: colors,
                    borderWidth: 3,
                    borderColor: '#ffffff',
                    hoverOffset: 6,
                },
            ],
        };
    };

    const pieOptions: ChartOptions<'pie'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: '#475569',
                    boxWidth: 10,
                    boxHeight: 10,
                    padding: 16,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    font: {
                        size: 11,
                        weight: 500,
                    },
                },
            },
            tooltip: {
                displayColors: false,
                backgroundColor: '#111827',
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    label: (tooltipItem) => {
                        const value = Number(tooltipItem.raw || 0);

                        return value === 1
                            ? '1 escaneo'
                            : `${formatNumber(value)} escaneos`;
                    },
                },
            },
        },
    };

    const renderTypeCard = (
        type: TypeKeys,
        acceptedOrNot: AcceptedOrNot
    ) => {
        const config = TYPE_CONFIG[type];

        const value =
            acceptedOrNot === 'accepted'
                ? totals.accepted[type]
                : totals.rejected[type];

        return (
            <div className="stats-type-card" key={type}>
                <div
                    className="stats-type-card__icon"
                    style={{
                        backgroundColor: config.background,
                        color: config.color,
                    }}
                >
                    <span />
                </div>

                <div className="stats-type-card__content">
                    <span className="stats-type-card__label">
                        {config.label}
                    </span>

                    <strong>
                        {formatNumber(value)}
                    </strong>

                    <span className="stats-type-card__caption">
                        escaneos
                    </span>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="stats-page">
                <div className="stats-loading">
                    <div className="stats-loading__spinner" />
                    <span>Obteniendo estadísticas...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="stats-page">
            <div className="stats-page__header">
                <div>
                    <div className="stats-page__eyebrow">
                        ANALÍTICA DEL EVENTO
                    </div>

                    <h1>
                        Estadísticas de {title}
                    </h1>

                    <p>
                        Resumen de actividad, aceptación y
                        distribución de escaneos.
                    </p>
                </div>

                <button
                    className="stats-back-button"
                    onClick={() =>
                        navigate(
                            `/main/recintos/${clientId}/eventos`
                        )
                    }
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M15 19l-7-7 7-7" />
                    </svg>

                    Volver a eventos
                </button>
            </div>

            {error && (
                <div className="stats-alert stats-alert--error">
                    <i className="fas fa-circle-exclamation" />
                    {error}
                </div>
            )}

            {!error && statistics.length === 0 && (
                <div className="stats-empty">
                    <div className="stats-empty__icon">
                        <i className="fas fa-chart-column" />
                    </div>

                    <h2>No hay estadísticas todavía</h2>

                    <p>
                        No se han registrado escaneos para este
                        evento.
                    </p>
                </div>
            )}

            {!error && statistics.length > 0 && (
                <div className="stats-dashboard">

                    {/* KPI CARDS */}
                    <section className="stats-kpis">
                        <div className="stats-kpi stats-kpi--total">
                            <div className="stats-kpi__icon">
                                <i className="fas fa-chart-line" />
                            </div>

                            <div>
                                <span>Total escaneos</span>
                                <strong>
                                    {formatNumber(totals.total)}
                                </strong>
                            </div>
                        </div>

                        <div className="stats-kpi stats-kpi--accepted">
                            <div className="stats-kpi__icon">
                                <i className="fas fa-circle-check" />
                            </div>

                            <div>
                                <span>Aceptados</span>
                                <strong>
                                    {formatNumber(
                                        totals.acceptedTotal
                                    )}
                                </strong>
                            </div>

                            <small>
                                {totals.acceptanceRate}% del total
                            </small>
                        </div>

                        <div className="stats-kpi stats-kpi--rejected">
                            <div className="stats-kpi__icon">
                                <i className="fas fa-circle-xmark" />
                            </div>

                            <div>
                                <span>No aceptados</span>
                                <strong>
                                    {formatNumber(
                                        totals.rejectedTotal
                                    )}
                                </strong>
                            </div>
                        </div>

                        <div className="stats-kpi stats-kpi--invalid">
                            <div className="stats-kpi__icon">
                                <i className="fas fa-triangle-exclamation" />
                            </div>

                            <div>
                                <span>Códigos inválidos</span>
                                <strong>
                                    {formatNumber(totals.invalid)}
                                </strong>
                            </div>
                        </div>
                    </section>

                    {/* TOTAL */}
                    <section className="stats-section">
                        <div className="stats-section__header">
                            <div>
                                <span className="stats-section__eyebrow">
                                    ACTIVIDAD
                                </span>

                                <h2>
                                    Escaneos por hora
                                </h2>
                            </div>

                            <div className="stats-section__total">
                                <strong>
                                    {formatNumber(totals.total)}
                                </strong>
                                <span>escaneos</span>
                            </div>
                        </div>

                        <div className="stats-main-chart">
                            <Bar
                                data={generateBarData(
                                    'total',
                                    COLORS.primary
                                )}
                                options={barOptions}
                            />
                        </div>
                    </section>

                    {/* ACEPTADOS */}
                    <section className="stats-section">
                        <div className="stats-section__header">
                            <div>
                                <span className="stats-section__eyebrow stats-section__eyebrow--green">
                                    RESULTADO POSITIVO
                                </span>

                                <h2>Aceptados</h2>

                                <p>
                                    Distribución de los escaneos
                                    aceptados.
                                </p>
                            </div>

                            <div className="stats-section__total stats-section__total--green">
                                <strong>
                                    {formatNumber(
                                        totals.acceptedTotal
                                    )}
                                </strong>
                                <span>escaneos</span>
                            </div>
                        </div>

                        <div className="stats-overview-grid">
                            <div className="stats-chart-card stats-chart-card--pie">
                                <div className="stats-chart-card__title">
                                    <span>Distribución por tipo</span>
                                </div>

                                <div className="stats-pie-chart">
                                    <Pie
                                        data={generatePieData(
                                            'accepted'
                                        )}
                                        options={pieOptions}
                                    />
                                </div>
                            </div>

                            <div className="stats-chart-card">
                                <div className="stats-chart-card__title">
                                    <span>Aceptados por hora</span>
                                </div>

                                <div className="stats-chart">
                                    <Bar
                                        data={generateBarData(
                                            'accepted',
                                            COLORS.accepted
                                        )}
                                        options={barOptions}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="stats-type-grid">
                            {(
                                Object.keys(
                                    TYPE_CONFIG
                                ) as TypeKeys[]
                            ).map((type) =>
                                renderTypeCard(
                                    type,
                                    'accepted'
                                )
                            )}
                        </div>

                        <div className="stats-detail-grid">
                            {(
                                Object.keys(
                                    TYPE_CONFIG
                                ) as TypeKeys[]
                            ).map((type) => (
                                <div
                                    className="stats-chart-card"
                                    key={type}
                                >
                                    <div className="stats-chart-card__title">
                                        <span>
                                            {TYPE_CONFIG[type].label}
                                        </span>

                                        <strong>
                                            {formatNumber(
                                                totals.accepted[
                                                    type
                                                ]
                                            )}
                                        </strong>
                                    </div>

                                    <div className="stats-chart stats-chart--small">
                                        <Bar
                                            data={generateBarData(
                                                `${type}_accepted` as keyof Statistic,
                                                TYPE_CONFIG[type]
                                                    .color
                                            )}
                                            options={barOptions}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* NO ACEPTADOS */}
                    <section className="stats-section">
                        <div className="stats-section__header">
                            <div>
                                <span className="stats-section__eyebrow stats-section__eyebrow--red">
                                    RESULTADO NEGATIVO
                                </span>

                                <h2>No aceptados</h2>

                                <p>
                                    Escaneos rechazados y códigos
                                    inválidos.
                                </p>
                            </div>

                            <div className="stats-section__total stats-section__total--red">
                                <strong>
                                   {formatNumber(totals.rejectedTotal)}
                                </strong>

                                <span>escaneos</span>
                            </div>
                        </div>

                        <div className="stats-overview-grid">
                            <div className="stats-chart-card stats-chart-card--pie">
                                <div className="stats-chart-card__title">
                                    <span>Distribución del rechazo</span>
                                </div>

                                <div className="stats-pie-chart">
                                    <Pie
                                        data={generatePieData(
                                            'not_accepted',
                                            true
                                        )}
                                        options={pieOptions}
                                    />
                                </div>
                            </div>

                            <div className="stats-chart-card">
                                <div className="stats-chart-card__title">
                                    <span>No aceptados por hora</span>
                                </div>

                                <div className="stats-chart">
                                    <Bar
                                        data={generateBarData(
                                            'not_accepted',
                                            COLORS.rejected
                                        )}
                                        options={barOptions}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="stats-type-grid">
                            {(
                                Object.keys(
                                    TYPE_CONFIG
                                ) as TypeKeys[]
                            ).map((type) =>
                                renderTypeCard(
                                    type,
                                    'not_accepted'
                                )
                            )}

                            <div className="stats-type-card stats-type-card--invalid">
                                <div className="stats-type-card__icon">
                                    <i className="fas fa-triangle-exclamation" />
                                </div>

                                <div className="stats-type-card__content">
                                    <span className="stats-type-card__label">
                                        INVÁLIDOS
                                    </span>

                                    <strong>
                                        {formatNumber(
                                            totals.invalid
                                        )}
                                    </strong>

                                    <span className="stats-type-card__caption">
                                        códigos
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="stats-detail-grid stats-detail-grid--five">
                            {(
                                Object.keys(
                                    TYPE_CONFIG
                                ) as TypeKeys[]
                            ).map((type) => (
                                <div
                                    className="stats-chart-card"
                                    key={type}
                                >
                                    <div className="stats-chart-card__title">
                                        <span>
                                            {TYPE_CONFIG[type].label}
                                        </span>

                                        <strong>
                                            {formatNumber(
                                                totals.rejected[
                                                    type
                                                ]
                                            )}
                                        </strong>
                                    </div>

                                    <div className="stats-chart stats-chart--small">
                                        <Bar
                                            data={generateBarData(
                                                `${type}_not_accepted` as keyof Statistic,
                                                TYPE_CONFIG[type]
                                                    .color
                                            )}
                                            options={barOptions}
                                        />
                                    </div>
                                </div>
                            ))}

                            <div className="stats-chart-card">
                                <div className="stats-chart-card__title">
                                    <span>Inválidos</span>

                                    <strong>
                                        {formatNumber(
                                            totals.invalid
                                        )}
                                    </strong>
                                </div>

                                <div className="stats-chart stats-chart--small">
                                    <Bar
                                        data={generateBarData(
                                            'invalid_code',
                                            COLORS.invalid
                                        )}
                                        options={barOptions}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default EstadisticasEvento;